import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Dna, Sparkles, Shield, Zap, Brain, Heart, Activity } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { genotypesAPI } from '../lib/api';

const sectionConfig = {
  summary: {
    label: 'Summary',
    icon: Dna,
    color: '#8DA399',
  },
  physical_signature: {
    label: 'Physical Signature',
    icon: Activity,
    color: '#E8A095',
  },
  metabolic_engine: {
    label: 'Metabolic Engine',
    icon: Zap,
    color: '#E8C495',
  },
  psychological_profile: {
    label: 'Psychological Profile',
    icon: Brain,
    color: '#95B8E8',
  },
  health_vulnerabilities: {
    label: 'Health & Vulnerabilities',
    icon: Shield,
    color: '#D68C82',
  },
};

export default function GenotypePage() {
  const { genotypeId } = useParams();
  const [genotype, setGenotype] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadGenotype();
  }, [genotypeId]);

  const loadGenotype = async () => {
    try {
      const response = await genotypesAPI.getById(genotypeId);
      setGenotype(response.data);
    } catch (err) {
      setError('Failed to load genotype information');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !genotype) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-[#4A2C2A]/60">{error || 'Genotype not found'}</p>
          <Link to="/dashboard">
            <Button variant="ghost" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const hasHighlights = genotype.motto || genotype.greatest_strength || genotype.greatest_challenge;
  const sections = genotype.sections || {};
  const filledSections = Object.entries(sectionConfig).filter(([key]) => sections[key]);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="genotype-page">
        {/* Back Link */}
        <Link to="/dashboard" className="inline-flex items-center text-[#4A2C2A]/60 hover:text-[#4A2C2A]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Link>

        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl">
          {/* Background Image or Gradient */}
          <div className="absolute inset-0">
            {genotype.image_url ? (
              <img 
                src={genotype.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${genotype.image_url}` : genotype.image_url}
                alt={genotype.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#8DA399] to-[#4A2C2A]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </div>

          {/* Content */}
          <div className="relative z-10 p-8 pt-32 pb-10">
            <div className="flex items-center gap-2 mb-3">
              <Dna className="w-5 h-5 text-[#E8A095]" />
              <span className="text-sm text-[#E8A095] font-medium uppercase tracking-wider">Your Genotype</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">{genotype.name}</h1>
            
            {genotype.motto && (
              <p className="text-xl text-white/80 italic max-w-2xl">"{genotype.motto}"</p>
            )}
          </div>
        </div>

        {/* Highlights Cards */}
        {hasHighlights && (
          <div className="grid md:grid-cols-2 gap-6">
            {genotype.greatest_strength && (
              <div className="soft-card p-6 border-l-4 border-[#8DA399]">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-[#8DA399]" />
                  <span className="text-sm font-medium text-[#8DA399] uppercase tracking-wider">Greatest Strength</span>
                </div>
                <p className="text-lg text-[#4A2C2A]">{genotype.greatest_strength}</p>
              </div>
            )}

            {genotype.greatest_challenge && (
              <div className="soft-card p-6 border-l-4 border-[#E8A095]">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-[#E8A095]" />
                  <span className="text-sm font-medium text-[#E8A095] uppercase tracking-wider">Greatest Challenge</span>
                </div>
                <p className="text-lg text-[#4A2C2A]">{genotype.greatest_challenge}</p>
              </div>
            )}
          </div>
        )}

        {/* Profile Sections */}
        {filledSections.length > 0 && (
          <div className="space-y-6">
            {filledSections.map(([key, config]) => {
              const IconComponent = config.icon;
              return (
                <div key={key} className="soft-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${config.color}20` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: config.color }} />
                    </div>
                    <h2 className="font-heading text-xl text-[#4A2C2A]">{config.label}</h2>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-[#4A2C2A]/70 leading-relaxed whitespace-pre-wrap">
                      {sections[key]}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* No Content State */}
        {filledSections.length === 0 && !hasHighlights && (
          <div className="soft-card p-8 text-center">
            <Dna className="w-12 h-12 mx-auto mb-4 text-[#4A2C2A]/30" />
            <p className="text-[#4A2C2A]/60">
              Detailed profile information is being prepared by your wellness provider.
            </p>
          </div>
        )}

        {/* Personalized Recommendations CTA */}
        <div className="soft-card p-6 bg-gradient-to-r from-[#4A2C2A] to-[#2D1A19] text-white">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Heart className="w-8 h-8 text-[#E8A095]" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-heading text-xl mb-2">Personalized for Your Genotype</h3>
              <p className="text-white/70">
                Your diet, supplements, and lifestyle recommendations are tailored to your unique profile.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/diet">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full">
                  Diet
                </Button>
              </Link>
              <Link to="/supplements">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full">
                  Supplements
                </Button>
              </Link>
              <Link to="/lifestyle">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full">
                  Lifestyle
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Ask Advisor CTA */}
        <div className="text-center py-6">
          <p className="text-[#4A2C2A]/60 mb-4">Have questions about your genotype?</p>
          <Link to="/chat">
            <Button className="bg-[#E8A095] text-[#4A2C2A] hover:bg-[#D68C82] rounded-full px-8">
              Ask Your Wellness Advisor
            </Button>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
